/**
 * ProductWorkspace — product-first authoring workspace.
 *
 * Route: /admin/products/:productId
 * Anchors admin/editor inside ONE specific product's context and provides:
 *   • Обзор — product summary + counts
 *   • Библиотека — for VOLUME products, embeds the BookAdminPanel scoped to the volume
 *   • Настройки — product core fields (title, slug, status, description)
 *   • «Посмотреть как читатель» — opens the real reader view in a new tab
 *
 * Access:
 *   • Admin — full access to any product.
 *   • Editor — only to products in their `product_scope_ids`. 403 fallback otherwise.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ChevronLeft, ExternalLink, Save, Eye } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import BookAdminPanel from "@/components/admin/BookAdminPanel";

const PRODUCT_STATUS_OPTIONS = [
  { value: "draft",     label: "Черновик" },
  { value: "review",    label: "На проверке" },
  { value: "published", label: "Опубликовано" },
  { value: "hidden",    label: "Скрыто" },
];

export default function ProductWorkspace() {
  const { productId } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") || "overview";
  const [tab, setTab] = useState(initialTab);
  const [product, setProduct] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const isAdmin = user?.role === "admin";
  const isEditor = user?.role === "editor";
  const scopes = user?.product_scope_ids || [];
  const inScope = isAdmin || scopes.length === 0 || scopes.includes(productId);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/admin/products");
        const p = (data || []).find((x) => x.id === productId);
        if (!p) throw new Error("not-found");
        setProduct(p);
      } catch (e) {
        if (e.message === "not-found") {
          setError("Продукт не найден");
        } else {
          setError(formatApiError(e));
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [productId]);

  const changeTab = (v) => {
    setTab(v);
    const next = new URLSearchParams(searchParams);
    if (v === "overview") next.delete("tab"); else next.set("tab", v);
    setSearchParams(next, { replace: true });
  };

  const saveSettings = async () => {
    if (!product) return;
    setSaving(true);
    try {
      const patch = {
        title: product.title,
        subtitle: product.subtitle,
        short_description: product.short_description,
        status: product.status,
        is_public: !!product.is_public,
        sort_order: product.sort_order ?? 0,
      };
      const { data } = await api.patch(`/admin/products/${productId}`, patch);
      setProduct(data);
      toast.success("Сохранено");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const openAsReader = () => {
    if (!product) return;
    // For VOLUME products, reader lives in the Book domain.
    if (product.type === "VOLUME") {
      window.open(`/book/${encodeURIComponent(product.slug)}/entry`, "_blank");
    } else {
      window.open(`/products/${encodeURIComponent(product.slug)}`, "_blank");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center"
           data-testid="product-workspace-loading">
        <div className="opacity-60 text-sm">Загрузка продукта…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center px-6"
           data-testid="product-workspace-error">
        <div className="text-center max-w-md">
          <div className="font-serif text-2xl mb-3 text-espresso">{error}</div>
          <Button onClick={() => nav("/admin?tab=products")}
            data-testid="product-workspace-back-admin">
            К списку продуктов
          </Button>
        </div>
      </div>
    );
  }

  if (!inScope) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center px-6"
           data-testid="product-workspace-noscope">
        <div className="text-center max-w-md">
          <div className="font-serif text-2xl mb-3 text-espresso">Нет доступа к этому продукту</div>
          <div className="text-sm opacity-70 mb-6">
            Ваша роль {isEditor ? "Editor" : ""} не включает этот продукт в scope.
            Обратитесь к владельцу, чтобы получить доступ.
          </div>
          <Button variant="outline" onClick={() => nav("/admin?tab=products")}>
            Назад
          </Button>
        </div>
      </div>
    );
  }

  const isVolume = product?.type === "VOLUME";

  return (
    <div className="min-h-screen bg-cream" data-testid="product-workspace" data-product-id={productId}>
      {/* Header */}
      <header className="border-b border-espresso/10 bg-white/60 backdrop-blur sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-5 py-3 flex items-center gap-3">
          <button onClick={() => nav("/admin?tab=products")}
            className="p-2 -ml-2 rounded-full hover:bg-sand/50"
            aria-label="К списку продуктов"
            data-testid="product-workspace-back">
            <ChevronLeft className="w-5 h-5 text-espresso" />
          </button>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.28em] text-muted-foreground">
              Сейчас вы работаете в
            </div>
            <h1 className="font-serif text-xl md:text-2xl text-espresso truncate -mt-0.5"
                data-testid="product-workspace-title">
              {product.title}
            </h1>
          </div>
          <Button onClick={openAsReader} variant="outline"
            data-testid="product-workspace-open-reader"
            className="hidden md:inline-flex">
            <Eye className="w-4 h-4 mr-2" /> Как читатель
          </Button>
          <button onClick={openAsReader}
            className="md:hidden p-2 rounded-full border border-espresso/20"
            aria-label="Как читатель"
            data-testid="product-workspace-open-reader-mobile">
            <Eye className="w-4 h-4" />
          </button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-5 py-6">
        <Tabs value={tab} onValueChange={changeTab}>
          <div className="overflow-x-auto -mx-1 px-1">
            <TabsList className="rounded-full bg-sand/40 h-11 p-1 inline-flex w-max">
              <TabsTrigger value="overview" data-testid="pw-tab-overview" className="rounded-full">Обзор</TabsTrigger>
              {isVolume && (
                <TabsTrigger value="library" data-testid="pw-tab-library" className="rounded-full">Библиотека</TabsTrigger>
              )}
              <TabsTrigger value="settings" data-testid="pw-tab-settings" className="rounded-full">Настройки</TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="overview" className="mt-6" data-testid="pw-panel-overview">
            <OverviewPanel product={product} onOpenReader={openAsReader}
              onGoLibrary={() => changeTab("library")} isVolume={isVolume} />
          </TabsContent>

          {isVolume && (
            <TabsContent value="library" className="mt-4" data-testid="pw-panel-library">
              <BookAdminPanel productIdFilter={productId} />
            </TabsContent>
          )}

          <TabsContent value="settings" className="mt-6" data-testid="pw-panel-settings">
            <div className="max-w-2xl bg-white/60 border hairline rounded-2xl p-6 space-y-4">
              <div>
                <label className="block text-xs uppercase tracking-widest opacity-70 mb-1">Название</label>
                <Input value={product.title || ""}
                  onChange={(e) => setProduct({ ...product, title: e.target.value })}
                  data-testid="pw-field-title" />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest opacity-70 mb-1">Подзаголовок</label>
                <Input value={product.subtitle || ""}
                  onChange={(e) => setProduct({ ...product, subtitle: e.target.value })}
                  data-testid="pw-field-subtitle" />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest opacity-70 mb-1">Короткое описание</label>
                <textarea value={product.short_description || ""} rows={3}
                  onChange={(e) => setProduct({ ...product, short_description: e.target.value })}
                  className="w-full border rounded px-3 py-2 text-sm"
                  data-testid="pw-field-desc" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs uppercase tracking-widest opacity-70 mb-1">Статус</label>
                  <Select value={product.status || "draft"}
                    onValueChange={(v) => setProduct({ ...product, status: v })}>
                    <SelectTrigger data-testid="pw-field-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PRODUCT_STATUS_OPTIONS.map((s) => (
                        <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-widest opacity-70 mb-1">Порядок</label>
                  <Input type="number" value={product.sort_order ?? 0}
                    onChange={(e) => setProduct({ ...product, sort_order: parseInt(e.target.value || "0", 10) })} />
                </div>
              </div>
              <details className="border-t pt-3">
                <summary className="text-xs uppercase tracking-widest opacity-70 cursor-pointer">Дополнительные настройки</summary>
                <div className="mt-3 space-y-2">
                  <label className="block text-xs opacity-70 mb-1">Slug (URL)</label>
                  <Input value={product.slug || ""}
                    onChange={(e) => setProduct({ ...product, slug: e.target.value })}
                    data-testid="pw-field-slug" />
                  <label className="inline-flex items-center gap-2 mt-2 text-sm">
                    <input type="checkbox" checked={!!product.is_public}
                      onChange={(e) => setProduct({ ...product, is_public: e.target.checked })} />
                    <span>Публичный (виден без входа)</span>
                  </label>
                </div>
              </details>
              <div className="pt-4">
                <Button onClick={saveSettings} disabled={saving}
                  data-testid="pw-settings-save">
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? "Сохранение…" : "Сохранить"}
                </Button>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function OverviewPanel({ product, onOpenReader, onGoLibrary, isVolume }) {
  const statusLabel = PRODUCT_STATUS_OPTIONS.find((s) => s.value === product?.status)?.label || product?.status;
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="pw-overview">
      <div className="md:col-span-2 rounded-2xl bg-white/60 border hairline p-6">
        <div className="text-[10px] uppercase tracking-widest opacity-70 mb-2">
          {product?.type === "VOLUME" ? "Том" : product?.type}
        </div>
        <div className="font-serif text-3xl text-espresso mb-2">{product?.title}</div>
        {product?.subtitle && (
          <div className="italic text-lg opacity-80 mb-4">{product.subtitle}</div>
        )}
        {product?.short_description && (
          <p className="text-sm leading-relaxed opacity-80">{product.short_description}</p>
        )}
        <div className="mt-6 flex flex-wrap gap-2">
          {isVolume && (
            <Button onClick={onGoLibrary} data-testid="pw-overview-go-library">
              Управлять содержанием
            </Button>
          )}
          <Button variant="outline" onClick={onOpenReader}
            data-testid="pw-overview-open-reader">
            <ExternalLink className="w-4 h-4 mr-2" /> Открыть как читатель
          </Button>
        </div>
      </div>
      <div className="rounded-2xl bg-white/60 border hairline p-6 space-y-3">
        <div>
          <div className="text-xs uppercase tracking-widest opacity-70">Статус</div>
          <div className="font-serif text-lg">{statusLabel}</div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest opacity-70">Slug</div>
          <div className="font-mono text-sm">{product?.slug}</div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest opacity-70">Публичный</div>
          <div className="text-sm">{product?.is_public ? "Да" : "Нет"}</div>
        </div>
      </div>
    </div>
  );
}
